import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MessageSquare, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="pt-24 pb-16" data-testid="contact-page">
        <div className="max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16 fade-in">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Get in Touch</h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions, feedback, or suggestions? We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12 fade-in" data-testid="contact-form">
              <div className="flex items-center gap-3 mb-8">
                <MessageSquare className="w-8 h-8 text-purple-600" />
                <h2 className="text-2xl font-bold">Send us a Message</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3"
                    placeholder="Jane Doe"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3"
                    placeholder="jane@example.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 resize-none"
                    placeholder="Tell us what's on your mind..."
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 bg-purple-600 hover:bg-purple-700 text-white font-semibold text-base"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8 fade-in">
              <div className="bg-gradient-to-br from-purple-50 to-teal-50 rounded-3xl p-8">
                <Mail className="w-12 h-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Email Us</h3>
                <p className="text-sm text-gray-600 mb-4">
                  For general inquiries, partnerships, or support:
                </p>
                <a
                  href="mailto:stay-safe@safesheatlas.com"
                  className="text-base font-semibold text-purple-600 hover:text-purple-700"
                >
                  stay-safe@safesheatlas.com
                </a>
              </div>

              <div className="bg-white rounded-3xl shadow-lg p-8">
                <h3 className="text-xl font-bold mb-4">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-1">
                      How is the safety data collected?
                    </h4>
                    <p className="text-sm text-gray-600">
                      We aggregate data from government crime statistics, news APIs, and public safety reports, updated daily at midnight UTC.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-1">
                      Can I request coverage for a new city?
                    </h4>
                    <p className="text-sm text-gray-600">
                      Yes! Send us your request through this form, and we'll consider adding it to our coverage.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-1">
                      How do I unsubscribe from emails?
                    </h4>
                    <p className="text-sm text-gray-600">
                      Every email contains an unsubscribe link at the bottom. Click it to instantly stop receiving alerts.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-600 to-teal-600 rounded-3xl p-8 text-white">
                <h3 className="text-xl font-bold mb-3">Stay Connected</h3>
                <p className="text-sm leading-relaxed">
                  Join our community of safety-conscious travelers. Subscribe to daily alerts and never miss important safety updates for your destinations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;